import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { JSX } from "react";
import { Dialog } from "../Dialog/Dialog";
import { DialogContent } from "../Dialog/DialogContent";
import { DialogFooter } from "../Dialog/DialogFooter";
import { Card } from "../../Cards/Card/Card";
import { GridContainer } from "../../Layout/Grid/GridContainer";
import { GridItem } from "../../Layout/Grid/GridItem";
import { DropDownMenu } from "../../Inputs/DropDownMenu/DropDownMenu";
import { ButtonBar } from "../../Buttons/ButtonBar/ButtonBar";
import { RightButtons } from "../../Buttons/ButtonBar/RightButtons";
import { Button } from "../../Buttons/Button/Button";
import { SerendieSymbol, SymbolName } from "@serendie/symbols";
import "./Styles.scss";

export type ThemeId = 'konjo' | 'asagi' | 'kurikawa' | 'sumire' | 'tsutsuji' | 'dark' | 'high-contrast';

export class ThemeDialog extends BaseComponent<IThemeDialogProps, IThemeDialogState>
{
    constructor(props: IThemeDialogProps)
    {
        super(props);
        this.state = {
            selectedThemeId: props.themeId,
        };
    }

    public componentDidUpdate(prevProps: IThemeDialogProps): void
    {
        if (!prevProps.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        this.setState({ selectedThemeId: this.props.themeId });
    }

    private get themes(): IThemeOption[]
    {
        return [
            { id: 'konjo',         color: '#0650A0', label: this.props.konjoLabel },
            { id: 'asagi',         color: '#00757d', label: this.props.asagiLabel },
            { id: 'kurikawa',      color: '#813410', label: this.props.kurikawaLabel },
            { id: 'sumire',        color: '#733b85', label: this.props.sumireLabel },
            { id: 'tsutsuji',      color: '#f5c0d0', label: this.props.tsutsujiLabel },
            { id: 'dark',          symbol: 'moon',   label: this.props.darkLabel },
            { id: 'high-contrast', symbol: 'eye',    label: this.props.highContrastLabel },
        ];
    }

    private handleSelect(value: ThemeId): void
    {
        this.setState({ selectedThemeId: value });
    }

    private handleSave(): void
    {
        if (this.state.selectedThemeId != this.props.themeId)
        {
            this.props.onChange?.(this.state.selectedThemeId);
        }
        this.props.onClose();
    }

    public render(): JSX.Element
    {
        const themes = this.themes;
        const selected = themes.find(i => i.id == this.state.selectedThemeId) ?? themes[0];

        return (
            <Dialog
                isOpen={this.props.open}
                onOpenChange={() => this.props.onClose()}
                width={4}
                className="theme-dialog"
            >
                <DialogContent>
                    <Card
                        title={this.props.title}
                        subTitle={this.props.subTitle}
                    >
                        <GridContainer>
                            <GridItem>
                                <DropDownMenu
                                    label={this.props.themeInputLabel}
                                    placeholder={selected.label}
                                    onSelect={e => this.handleSelect(e.value as ThemeId)}
                                    items={themes.map(i => ({
                                        value: i.id,
                                        label: i.label,
                                        icon: i.color
                                            ? <SerendieSymbol name='circle' variant='filled' color={i.color} />
                                            : <SerendieSymbol name={i.symbol!} variant='filled' />,
                                    }))}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.onClose()}
                            >
                                {this.props.cancelButtonLabel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                            >
                                {this.props.saveButtonLabel}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        );
    }
}

export interface IThemeDialogProps
{
    open: boolean;
    themeId: ThemeId;
    onClose: () => void;
    onChange?: (themeId: ThemeId) => void;
    title: string;
    subTitle: string;
    themeInputLabel: string;
    konjoLabel: string;
    asagiLabel: string;
    kurikawaLabel: string;
    sumireLabel: string;
    tsutsujiLabel: string;
    darkLabel: string;
    highContrastLabel: string;
    saveButtonLabel: string;
    cancelButtonLabel: string;
}

export interface IThemeDialogState
{
    selectedThemeId: ThemeId;
}

interface IThemeOption
{
    id: ThemeId;
    label: string;
    color?: string;
    symbol?: SymbolName;
}
